/**
 * AbmhcEmbedCard — copy-paste embed snippets for the ABMHC Featured
 * Media block. Mirrors the pattern from BookshopAdmin > Embed tab.
 *
 * Shown at the top of the ABMHC admin so the user always sees exactly
 * which snippet to paste on their one.com PHP site (or anywhere else).
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Code2, ExternalLink } from 'lucide-react';

const backend = process.env.REACT_APP_BACKEND_URL;

const SNIPPETS = {
  script: `<!-- 1. Drop this anywhere you want the "Hope Is Rising in Zimbabwe" block to appear -->
<div id="abmhc-featured-media"></div>

<!-- 2. Include the widget once before </body> -->
<script src="${backend}/embed/abmhc/featured-media.js"
        data-target="#abmhc-featured-media"
        data-api="${backend}"
        data-theme="dark"
        defer></script>`,
  iframe: `<iframe src="${backend}/embed/abmhc/featured-media.html?api=${backend}&theme=dark"
        style="border:0;width:100%;height:640px"
        title="ABMHC — Hope Is Rising in Zimbabwe"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen">
</iframe>`,
  onecom: `<?php $ecoUrl = "${backend}"; ?>
<div id="abmhc-featured-media"></div>
<script src="<?=$ecoUrl?>/embed/abmhc/featured-media.js"
        data-target="#abmhc-featured-media"
        data-api="<?=$ecoUrl?>"
        data-theme="dark" defer></script>`,
};

const Row = ({ label, code, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="relative">
        <pre className="bg-slate-900 border border-slate-800 text-emerald-300 p-3 rounded text-xs overflow-x-auto whitespace-pre"
             data-testid={testid}>{code}</pre>
        <Button size="sm" variant="outline"
                className="absolute top-2 right-2 border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={copy}
                data-testid={`${testid}-copy`}>
          {copied ? 'Copied' : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
        </Button>
      </div>
    </div>
  );
};

export default function AbmhcEmbedCard() {
  return (
    <Card className="bg-slate-900/60 border-amber-500/30" data-testid="abmhc-embed-card">
      <CardHeader>
        <CardTitle className="text-sm text-white flex items-center gap-2">
          <Code2 className="h-4 w-4 text-amber-300" /> Embed code — paste on abmhc.org / one.com / anywhere
        </CardTitle>
        <CardDescription className="text-slate-400">
          Once you publish a Featured Media row below, these snippets render it live everywhere they&apos;re pasted.
          Nothing to configure — content, thumbnail, CTAs and analytics are all controlled from this page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Row label="① Drop-in <script> (recommended for HTML, WordPress, one.com PHP, etc.)"
             code={SNIPPETS.script}
             testid="abmhc-embed-script" />
        <Row label="② <iframe> embed (fully isolated — best when host CSS/JS might interfere)"
             code={SNIPPETS.iframe}
             testid="abmhc-embed-iframe" />
        <Row label="③ One.com PHP hero-injection snippet (used by /app/deploy/abmhc-onecom/index.php)"
             code={SNIPPETS.onecom}
             testid="abmhc-embed-onecom" />

        <div className="grid md:grid-cols-3 gap-2 text-xs">
          <a href={`${backend}/embed/abmhc/featured-media.js`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="abmhc-embed-open-js">
            <span><code>featured-media.js</code></span><ExternalLink className="h-3 w-3" />
          </a>
          <a href={`${backend}/embed/abmhc/featured-media.html`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="abmhc-embed-open-html">
            <span><code>featured-media.html</code></span><ExternalLink className="h-3 w-3" />
          </a>
          <a href={`${backend}/embed/abmhc/README.md`} target="_blank" rel="noreferrer"
             className="border border-slate-800 rounded p-2 text-slate-300 hover:bg-slate-800 flex items-center justify-between"
             data-testid="abmhc-embed-open-readme">
            <span><code>README.md</code> (full guide)</span><ExternalLink className="h-3 w-3" />
          </a>
        </div>

        <div className="border border-emerald-800/40 bg-emerald-900/10 text-emerald-200 rounded p-3 text-xs">
          <p><strong>Data source:</strong> <a className="underline"
              href={`${backend}/api/public/abmhc/featured-media`} target="_blank" rel="noreferrer">
              <code>{backend}/api/public/abmhc/featured-media</code></a> —
              the embed script fetches this on every page view.</p>
          <p className="mt-1"><strong>Full site JSON</strong> (used by the one.com PHP template):
              <a className="underline ml-1"
              href={`${backend}/api/v1/sites/abmhc/public`} target="_blank" rel="noreferrer">
              <code>{backend}/api/v1/sites/abmhc/public</code></a></p>
        </div>
      </CardContent>
    </Card>
  );
}
